import { NgClass } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  contentChildren,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { SelectOptionComponent } from './select-option/select-option.component';

@Component({
  selector: 'app-select',
  imports: [NgbDropdownModule, NgClass, TranslateModule],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent implements ControlValueAccessor, AfterContentInit {
  private cdr = inject(ChangeDetectorRef);

  public selected = input<any>(undefined);
  public changed = output<any>();
  public placeholder = input<string>('shared.select.placeholder');
  public header = input<boolean>(false);
  public size = input<string>('md');
  public status = input<string>(null);
  public disabled = model<boolean>(false);

  protected buttonWidth = signal<number>(0);

  protected searchValue = signal<string>('');
  protected selectedOption = signal<SelectOptionComponent>(null);

  private dropdownToggle = viewChild<ElementRef>('dropdownToggle');
  private options = contentChildren<SelectOptionComponent>(
    forwardRef(() => SelectOptionComponent),
    {
      descendants: true,
    }
  );
  private dropdown = viewChild<NgbDropdown>('dropdown');

  private onChange: any = () => {};
  private onTouched: any = () => {};

  private ngControl = inject(NgControl, { self: true, optional: true });

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      if (this.ngControl?.value !== undefined && this.options()?.length > 0) {
        this.select(
          this.options().find((o) => o.value() === this.ngControl.value),
          false
        );
      }
    });
  }

  ngAfterContentInit(): void {
    if (this.ngControl) {
      this.select(
        this.options().find((o) => o.value() === this.ngControl.value),
        false
      );
    } else if (this.selected() !== undefined) {
      this.select(
        this.options().find((o) => o.value() === this.selected()) ??
          this.options().find((o) => !o.value()),
        false
      );
    }
  }

  writeValue(obj: any): void {
    this.select(
      this.options().find((o) => o.value() === obj),
      false
    );
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public select(option: SelectOptionComponent, toggleChange = true) {
    this.options().forEach((o) => {
      o.selected.set(option === o);
    });

    this.selectedOption.set(option);

    if (toggleChange) {
      this.onChange(option?.value());
      this.changed.emit(option?.value());
    }

    this.dropdown()?.close();

    this.cdr.markForCheck();
  }

  protected isOpen() {
    return this.dropdown()?.isOpen();
  }

  protected onToggle() {
    this.buttonWidth.set(this.dropdownToggle().nativeElement.offsetWidth);
  }

  protected search(searchValue: string) {
    this.searchValue.set(searchValue.toLowerCase());

    if (!this.searchValue()) {
      this.options().forEach((option) => {
        option.isVisible.set(true);
      });

      return;
    }

    this.options().forEach((option) => {
      option.isVisible.set(
        option.label()?.toLowerCase().includes(this.searchValue())
      );
    });
  }
}
